import { defineComponent as _defineComponent } from 'vue'
import useHttpRequest from "@/composables/use-http-request";
import { ref, computed, watch, onUnmounted, Ref } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { Loading, MessageBox } from "element-ui";
import CourseWork from "@/interfaces/courseWork.interface";
import { isBrowserSupported } from "@/helpers/isBrowserSupported";
import BrowserNotSupportedWarningDialog from "@/components/dialogs/browser-not-supported-warning-dialog.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'start-course-work-component',
  props: {
    course_work: null
  },
  setup(__props: any) {

const props = __props as {
  course_work?: CourseWork;
}

const courseWorkStartInSeconds: Ref<number> = ref(0);
let countDownInterval: number | undefined;
const browserNotSupportedWarningDialogVisible = ref(false);

const route = useRoute();
const router = useRouter();



const formattedDateWithTime = (seconds: number) => {
  //Ensure we only start the Count down if it's less than a day
  return seconds >= 86400
    ? "Course Work Not Available"
    : `Starts in ${new Date(seconds * 1000).toISOString().substr(11, 8)}`;
};

const hasStudentStartedCourseWork = computed((): boolean => {
  return !!props.course_work?.student_course_work_submissions?.length;
});

const studentHasSpecialSubmitPermission = computed((): boolean => {
  return (
    hasStudentStartedCourseWork.value &&
    !!props.course_work?.student_course_work_submissions?.[0]
      ?.is_special_submission_valid
  );
});

const studentSubmitted = computed((): boolean => {
  return (
    hasStudentStartedCourseWork.value &&
    !!props.course_work?.student_course_work_submissions?.[0]?.submitted_at
  );
});

const canResubmitCourseWork = computed((): boolean => {
  return !!props.course_work?.is_available && studentSubmitted.value;
});

const buttonText = computed(() => {
  //first check for special permission
  if (studentHasSpecialSubmitPermission.value) {
    return "Resume Course Work";
  }

  if (props.course_work?.is_available) {
    if (studentSubmitted.value) {
      return "Already Submitted";
    } else if (hasStudentStartedCourseWork.value) {
      return "Resume Course Work";
    } else {
      return "Start Course Work";
    }
  } else {
    if (props.course_work?.start_in_seconds) {
      return formattedDateWithTime(courseWorkStartInSeconds.value);
    }
    //Meaning Now this Course work is in the Past
    else {
      //Find Out, if the  Student had not started the Course work Even. and it's not in the future
      if (
        !hasStudentStartedCourseWork.value &&
        props.course_work?.is_course_work_ended
      ) {
        return "You missed this Course Work";
      }

      if (studentSubmitted.value) {
        return "Already Submitted";
      }

      return "Not Available";
    }
  }
});

const buttonType = computed((): string => {
  if (buttonText.value === "Already Submitted") {
    return "success";
  } else if (buttonText.value === "You missed this Course Work") {
    return "warning";
  }

  return "primary";
});

const buttonIcon = computed(() => {
  if (buttonText.value === "Already Submitted") {
    return "el-icon-circle-check";
  } else if (buttonText.value === "You missed this Course Work") {
    return "el-icon-warning-outline";
  }
  return "";
});

const startCountDown = () => {
  countDownInterval = setInterval(() => {
    if (courseWorkStartInSeconds.value == 0) {
      clearInterval(countDownInterval);
      return location.reload();
    }
    --courseWorkStartInSeconds.value;
  }, 1000);
};

const ensureBrowserIsSupported = (): boolean => {
  if (!isBrowserSupported()) {
    browserNotSupportedWarningDialogVisible.value = true;
    return false;
  }

  return true;
};

const onStartCourseWork = async () => {
  if (
    !props.course_work?.is_available &&
    !studentHasSpecialSubmitPermission.value
  ) {
    return;
  }

  //But only proceed if the browser is supported
  if (!ensureBrowserIsSupported()) {
    return;
  }

  //Ensure the student has a submission and they havent submitted or they have a special submission pass
  if (
    studentHasSpecialSubmitPermission.value ||
    (hasStudentStartedCourseWork.value && !studentSubmitted.value)
  ) {
    //As A Hack, Forcefully Load The Course Work Screen As A New Html Page Since In That Way, Tinymce Loads Well
    return (window.location.href = `/dashboard/course-work/ongoing/${props.course_work?.course_work_id}`);
  }

  let loadingIndicator = Loading.service({
    lock: true,
    text: "Starting Course Work...",
    background: "rgba(0, 0, 0, 0.7)",
  });

  const request = await useHttpRequest<{ success: boolean; message: string }>({
    method: "POST",
    url: "course-work/start-course-work/",
    loadingProperty: null,
    showSuccessMessage: false,
    showFullScreenLoader: false,
    errorLoadingProperty: null,
    body: {
      course_work_id: props.course_work?.course_work_id,
      course_unit_id: route.params.courseUnitId,
    },
  });

  if (!request) {
    loadingIndicator.close();
    return;
  }

  if (
    request.success &&
    request.message === "Course Work Started Successfully"
  ) {
    //As A Hack, Forcefully Load The Course Work Screen As A New Html Page Since In That Way, Tinymce Loads Well
    window.location.href = `/dashboard/course-work/ongoing/${props.course_work?.course_work_id}`;
  } else {
    loadingIndicator.close();
    router.replace({ path: "/dashboard/course-works" });
  }
};

const fetchedCourseWork = computed((): CourseWork | null => {
  return props.course_work ?? null;
});

const onResubmitCourseWorkIntent = async () => {
  const confirm = await MessageBox.alert(
    "You've already submitted this course work. Are you sure you want to submit it again? Resubmissions are only allowed while the course work is still ongoing",
    {
      title: "Resubmit Answer Sheet?",
      confirmButtonText: "Yes, Submit Again",
      cancelButtonText: "Cancel",
      showCancelButton: true,
      distinguishCancelAndClose: true,
      closeOnClickModal: true,
      closeOnPressEscape: true,
    }
  );

  if (confirm === "confirm") {
    let loadingIndicator = Loading.service({
      lock: true,
      text: "Please Wait...",
      background: "rgba(0, 0, 0, 0.7)",
    });

    const request = await useHttpRequest<{
      success: boolean;
      message: string;
      course_work: CourseWork;
    }>({
      method: "GET",
      url: `course-work/ongoing-course-work/${
        props.course_work?.course_work_id
      }?timestamp=${new Date().getTime()}`,
      loadingProperty: null,
      showSuccessMessage: false,
      errorLoadingProperty: null,
      showFullScreenLoader: false,
    });

    if (!request) {
      loadingIndicator.close();
      return;
    }

    if (
      request.success &&
      request.message == "ONGOING COURSE WORK FETCHED SUCCESSFULLY"
    ) {
      window.location.href = `/dashboard/course-work/ongoing/${props.course_work?.course_work_id}`;
    } else {
      return loadingIndicator.close();
    }
  }
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
watch(fetchedCourseWork, () => {
  if (props.course_work?.start_in_seconds) {
    courseWorkStartInSeconds.value = props.course_work.start_in_seconds;
    startCountDown();
  }
});

onUnmounted(() => {
  if (countDownInterval) {
    clearInterval(countDownInterval);
  }
});

return { __sfc: true,courseWorkStartInSeconds, countDownInterval, browserNotSupportedWarningDialogVisible, route, router, props, formattedDateWithTime, hasStudentStartedCourseWork, studentHasSpecialSubmitPermission, studentSubmitted, canResubmitCourseWork, buttonText, buttonType, buttonIcon, startCountDown, ensureBrowserIsSupported, onStartCourseWork, fetchedCourseWork, onResubmitCourseWorkIntent, BrowserNotSupportedWarningDialog }
}

})